import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SocketManagerProvider } from '../socket-manager/socket-manager';
import { ACTIONS } from '../../bayadic-shared-models/enums/Enums';
import { GetSubscriptionStatusRequest } from '../../bayadic-shared-models/subscription/GetSubscriptionStatusRequest';
import { GetSubscriptionStatusResponse } from '../../bayadic-shared-models/subscription/GetSubscriptionStatusResponse';
import { Chat } from '../../bayadic-shared-models/chat/Chat';
import { UserManagerProvider } from '../user-manager/user-manager';
import { StartPayPalSubscriptionRequest } from '../../bayadic-shared-models/subscription/subscribe-request';
import { SubscriptionPlan } from '../../bayadic-shared-models/item/get-subscription-plans-response';
import { GetSubscriptionPlansResponse } from '../../bayadic-shared-models/item/get-subscription-plans-response';
import { GetSubscriptionPlansRequest } from '../../bayadic-shared-models/item/get-subscription-plans-request';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { SubscribeResponse } from '../../bayadic-shared-models/subscription/subscribe-response';
import { GetActiveSubscriptionsByUserIdRequest } from '../../bayadic-shared-models/item/get-active-subsciptions-by-user-id-request';
import { GetActiveSubscriptionsByUserIdResponse } from '../../bayadic-shared-models/item/get-active-subsciptions-by-user-id-response';
import { CancelSubscriptionResponse } from '../../bayadic-shared-models/subscription/cancel-subscribe-response';
import { CancelSubscriptionRequest } from '../../bayadic-shared-models/subscription/cancel-subscribe-request';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 5000; // 5 seconds

  constructor(
    private socketService: SocketManagerProvider, 
    private userManager: UserManagerProvider,
    private http: HttpClient
  ) {
    // Initialize subscription sync on service creation
    this.syncPendingSubscriptions();
  }

  private async syncPendingSubscriptions() {
    const pendingSubscriptions = this.getPendingSubscriptions();
    for (const subscription of pendingSubscriptions) {
      await this.retrySyncSubscription(subscription);
    }
  }

  private getPendingSubscriptions(): StartPayPalSubscriptionRequest[] {
    const stored = localStorage.getItem('pendingSubscriptions');
    return stored ? JSON.parse(stored) : [];
  }

  private savePendingSubscription(subscription: StartPayPalSubscriptionRequest) {
    const pending = this.getPendingSubscriptions();
    pending.push(subscription);
    localStorage.setItem('pendingSubscriptions', JSON.stringify(pending));
  }

  private removePendingSubscription(subscriptionId: string) {
    const pending = this.getPendingSubscriptions();
    const updated = pending.filter(sub => sub.planId !== subscriptionId);
    localStorage.setItem('pendingSubscriptions', JSON.stringify(updated));
  }

  private async retrySyncSubscription(subscription: StartPayPalSubscriptionRequest, attempt = 1): Promise<boolean> {
    try {
      await this.socketService.asyncQuery<StartPayPalSubscriptionRequest, StartPayPalSubscriptionRequest>(
        ACTIONS.startPaypalSubscription,
        subscription
      );
      this.removePendingSubscription(subscription.planId);
      return true;
    } catch (error) {
      if (attempt < this.MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY));
        return this.retrySyncSubscription(subscription, attempt + 1);
      }
      return false;
    }
  }

  async startSubscription(subscriptionRequest: StartPayPalSubscriptionRequest) {
    try {
      const response = await this.socketService.asyncQuery<SubscribeResponse, StartPayPalSubscriptionRequest>(
        ACTIONS.startPaypalSubscription,
        subscriptionRequest
      );
      return response;
    } catch (error) {
      // If server is down, store subscription data locally
      this.savePendingSubscription(subscriptionRequest);
      throw error;
    }
  }

  public async getSubscriptionStatus(userId: string): Promise<GetSubscriptionStatusResponse> {
    const response = await this.socketService.
    asyncQuery<GetSubscriptionStatusResponse, GetSubscriptionStatusRequest>
    (ACTIONS.getSubscriptionStatus, { userId });
    console.log("response", response);
    return response
;
  }
  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    const userId = this.userManager.user.userID;
    const response = await this.socketService.
    asyncQuery<GetSubscriptionPlansResponse, GetSubscriptionPlansRequest>
    (ACTIONS.getSubscriptionPlans, { userId });
    console.log("response", response);
    return response.subscriptionPlans
;
  }

  public async checkLessonAccess(chat: Chat): Promise<boolean> {
    if (!chat.isLesson()) {
      return true;
    }
    

    const subscriptionStatus = await this.getSubscriptionStatus(this.userManager.user.userID);
    return subscriptionStatus.isSubscribed;
  }

  async getSubscriptionsByUserId(userId: string): Promise<GetActiveSubscriptionsByUserIdResponse> {
    try {
      const response = await this.socketService.asyncQuery<GetActiveSubscriptionsByUserIdResponse, GetActiveSubscriptionsByUserIdRequest>(
        ACTIONS.getActiveSubscriptionsByUserId, { userId }
      );
      return response;
    } catch (error) {
      console.error('Error fetching subscription by custom ID:', error);
      throw error;
    }
  }


  async cancelSubscription(subscriptionId: string, planId: string, userId: string): Promise<void> {
    try {
      await this.socketService.asyncQuery<CancelSubscriptionResponse, CancelSubscriptionRequest>  (
        ACTIONS.cancelSubscription,
          { subscriptionId, planId, userId }
      );
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      throw error;
    }
  }
} 